import { apiBlobRequest, apiRequest } from '../api/http'
import type { BlobDownload } from '../api/http'
import type { NoonOrderOperations, OrderDetail, OrderExportParams, OrderListItem, OrderListParams, OrderPage, OrderSummary, OrderZohoSync, ZohoPolicyPreview } from './types'

const ORDERS_BASE = '/api/orders'

function orderAuth(accessToken: string) {
  return { accessToken }
}

function appendOrderFilters(search: URLSearchParams, params: OrderExportParams): void {
  if (params.query?.trim()) search.set('query', params.query.trim())
  if (params.status) search.set('status', params.status)
  if (params.provider) search.set('provider', params.provider)
  if (params.channelConnectionId != null) search.set('channelConnectionId', String(params.channelConnectionId))
  if (params.zohoStatus) search.set('zohoStatus', params.zohoStatus)
  // Date inputs use local calendar days, just like the displayed order dates.
  for (const key of ['dateFrom', 'dateTo'] as const) {
    if (!params[key]) continue
    const boundary = new Date(`${params[key]}T00:00:00`)
    if (key === 'dateTo') boundary.setDate(boundary.getDate() + 1)
    search.set(key, boundary.toISOString())
  }
}

function querySuffix(search: URLSearchParams): string {
  const query = search.toString()
  return query ? `?${query}` : ''
}

function listQuery(params: OrderListParams = {}): string {
  const search = new URLSearchParams()
  if (params.page != null) search.set('page', String(params.page))
  if (params.size != null) search.set('size', String(params.size))
  appendOrderFilters(search, params)
  return querySuffix(search)
}

function exportQuery(params: OrderExportParams = {}): string {
  const search = new URLSearchParams()
  appendOrderFilters(search, params)
  return querySuffix(search)
}

export function listOrders(accessToken: string, params: OrderListParams = {}): Promise<OrderPage<OrderListItem>> {
  return apiRequest<OrderPage<OrderListItem>>(ORDERS_BASE, listQuery(params), orderAuth(accessToken))
}

export function exportOrders(accessToken: string, params: OrderExportParams = {}): Promise<BlobDownload> {
  return apiBlobRequest(ORDERS_BASE, `/export${exportQuery(params)}`, orderAuth(accessToken))
}

export function getOrder(accessToken: string, id: number): Promise<OrderDetail> {
  return apiRequest<OrderDetail>(ORDERS_BASE, `/${id}`, orderAuth(accessToken))
}

export function refreshOrderFromChannel(accessToken: string, id: number): Promise<OrderDetail> {
  return apiRequest<OrderDetail>(ORDERS_BASE, `/${id}/refresh`, { ...orderAuth(accessToken), method: 'POST' })
}

export function getOrderSummary(accessToken: string): Promise<OrderSummary> {
  return apiRequest<OrderSummary>(ORDERS_BASE, '/summary', orderAuth(accessToken))
}

export function getZohoPolicyPreview(accessToken: string, id: number, policyId?: number): Promise<ZohoPolicyPreview> {
  const query = policyId == null ? '' : `?policyId=${policyId}`
  return apiRequest<ZohoPolicyPreview>(ORDERS_BASE, `/${id}/zoho/preview${query}`, orderAuth(accessToken))
}

export function sendOrderToZoho(accessToken: string, id: number): Promise<OrderZohoSync> {
  return apiRequest<OrderZohoSync>(ORDERS_BASE, `/${id}/zoho/send`, { ...orderAuth(accessToken), method: 'POST' })
}

export function resetOrderZohoInvoiceSync(accessToken: string, id: number): Promise<OrderZohoSync> {
  return apiRequest<OrderZohoSync>(ORDERS_BASE, `/${id}/zoho/invoice/reset`, { ...orderAuth(accessToken), method: 'POST' })
}

export function resetOrderZohoSync(accessToken: string, id: number): Promise<OrderZohoSync> {
  return apiRequest<OrderZohoSync>(ORDERS_BASE, `/${id}/zoho/reset`, { ...orderAuth(accessToken), method: 'POST' })
}

export function getNoonOrderOperations(accessToken: string, id: number): Promise<NoonOrderOperations> {
  return apiRequest<NoonOrderOperations>(ORDERS_BASE, `/${id}/operations`, orderAuth(accessToken))
}

export function updateNoonAvailability(accessToken: string, id: number, lines: Array<{ lineItemId: number, available: boolean }>): Promise<NoonOrderOperations> {
  return apiRequest<NoonOrderOperations>(ORDERS_BASE, `/${id}/operations/availability`, { ...orderAuth(accessToken), method: 'PUT', body: { lines } })
}

export function associateNoonBag(accessToken: string, id: number, bagCode: string, lineItemIds: number[]): Promise<NoonOrderOperations> {
  return apiRequest<NoonOrderOperations>(ORDERS_BASE, `/${id}/operations/bag`, { ...orderAuth(accessToken), method: 'POST', body: { bagCode, lineItemIds } })
}

export function createNoonShipment(accessToken: string, id: number): Promise<NoonOrderOperations> {
  return apiRequest<NoonOrderOperations>(ORDERS_BASE, `/${id}/operations/create`, { ...orderAuth(accessToken), method: 'POST' })
}

export function reconcileNoonShipment(accessToken: string, id: number): Promise<NoonOrderOperations> {
  return apiRequest<NoonOrderOperations>(ORDERS_BASE, `/${id}/operations/reconcile`, { ...orderAuth(accessToken), method: 'POST' })
}

export function cancelNoonShipment(accessToken: string, id: number): Promise<NoonOrderOperations> {
  return apiRequest<NoonOrderOperations>(ORDERS_BASE, `/${id}/operations/cancel`, { ...orderAuth(accessToken), method: 'POST' })
}
